// ============================================================================
// CR071: BRAND ASSET UTILITIES (Updated to use resolver)
// Now delegates to merchantLogoResolver as the single entry point
// Maintains backwards compatibility with existing API
// ============================================================================

import { 
  LOGO_REGISTRY, 
  normalizeToRegistryKey,
  getMerchantLogoPath,
  getPurchaseBrandLogoPath,
  type LogoEntry
} from "./logoRegistry";

import {
  resolveMerchantLogo,
  resolvePurchaseBrandLogo,
  resolveBrandLogo,
  getLogoFallbackInitial,
  hasValidLogo,
  type LogoResult,
} from "./merchantLogoResolver";

// Re-export types and functions from registry (backwards compat)
export { 
  LOGO_REGISTRY, 
  normalizeToRegistryKey as normalizeBrandKey,
  type LogoEntry 
} from "./logoRegistry";

// Re-export resolver types and functions
export {
  resolveMerchantLogo,
  resolvePurchaseBrandLogo,
  resolveBrandLogo,
  getLogoFallbackInitial,
  hasValidLogo,
  type LogoResult,
} from "./merchantLogoResolver";

/**
 * Get logo path for a purchase brand (gift card brand)
 * Returns local path from LOGO_REGISTRY only - never fetches external URLs
 * 
 * @deprecated Use resolvePurchaseBrandLogo() for full resolution chain
 */
export function getPurchaseBrandLogo(brandName?: string | null): string | null {
  if (!brandName) return null;
  return getPurchaseBrandLogoPath(brandName);
}

/**
 * Get logo path for a merchant
 * Returns local path from LOGO_REGISTRY only - never fetches external URLs
 * 
 * @deprecated Use resolveMerchantLogo() for full resolution chain (includes DB logos)
 */
export function getMerchantLogo(merchantName?: string | null): string | null {
  if (!merchantName) return null;
  
  // Use new resolver - returns path for url/local types
  const result = resolveMerchantLogo(merchantName);
  if (result.type === "url" || result.type === "local") {
    return result.src || null;
  }
  
  return null;
}

/**
 * Get any brand logo by name (tries merchant first, then purchase)
 * Returns local path from LOGO_REGISTRY only - never fetches external URLs
 * 
 * @deprecated Use resolveBrandLogo() for full resolution chain
 */
export function getBrandLogo(name?: string | null): string | null {
  if (!name) return null;
  
  // Try merchant first (includes DB lookup)
  const merchantResult = resolveMerchantLogo(name);
  if (merchantResult.type === "url" || merchantResult.type === "local") {
    return merchantResult.src || null;
  }
  
  // Then try purchase brand
  const purchaseResult = resolvePurchaseBrandLogo(name);
  if (purchaseResult.type === "local") {
    return purchaseResult.src || null;
  }
  
  return null;
}

/**
 * Check if a brand has a logo available
 * Now checks both DB and legacy registry
 */
export function hasBrandLogo(name?: string | null): boolean {
  if (!name) return false;
  const result = resolveBrandLogo(name);
  return hasValidLogo(result);
}

/**
 * Get the display name for a brand from the registry
 */
export function getBrandDisplayName(name: string): string {
  const key = normalizeToRegistryKey(name);
  return LOGO_REGISTRY[key]?.displayName ?? name;
}

/**
 * Get logo entry with full metadata (legacy registry only)
 */
export function getLogoEntry(name: string): LogoEntry | null {
  const key = normalizeToRegistryKey(name);
  return LOGO_REGISTRY[key] ?? null;
}
